import React, { useEffect, useState } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Loader2 } from "lucide-react";

// Routes that bypass the beta gate even when it's enabled — so users can
// always reach the entry point, log in, and admins can manage codes.
const PUBLIC_PATHS = ["/beta", "/login", "/register"];
const PUBLIC_PREFIXES = ["/api/"];

/**
 * Wraps the entire app. On mount:
 *  1. Fetch /settings/public — if `beta_gate_enabled === false`, render
 *     children unchanged.
 *  2. If enabled, fetch /beta/check. Allow if has_access. Allow always for
 *     admins (fetched via existing auth context).
 *  3. Otherwise redirect to /beta.
 *
 * Status is cached in sessionStorage so we don't add a request to every nav
 * — a single decision per browser session is enough.
 */
export default function BetaGate({ children }) {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState(() => {
    try {
      return sessionStorage.getItem("cc_beta_decision") || null; // "allow" | "block" | null
    } catch { return null; }
  });
  const [checking, setChecking] = useState(status === null);

  useEffect(() => {
    if (status !== null || authLoading) return;
    let alive = true;
    (async () => {
      try {
        const { data: settings } = await api.get("/settings/public");
        if (!settings.beta_gate_enabled) {
          if (alive) {
            sessionStorage.setItem("cc_beta_decision", "allow");
            setStatus("allow");
          }
          return;
        }
        // Gate is on. Admins always pass.
        if (user?.role === "admin") {
          if (alive) setStatus("allow");
          return;
        }
        const { data: check } = await api.get("/beta/check");
        if (alive) {
          const decision = check.has_access ? "allow" : "block";
          sessionStorage.setItem("cc_beta_decision", decision);
          setStatus(decision);
        }
      } catch {
        // Settings endpoint failed → fail open so the site never breaks
        if (alive) {
          sessionStorage.setItem("cc_beta_decision", "allow");
          setStatus("allow");
        }
      } finally {
        if (alive) setChecking(false);
      }
    })();
    return () => { alive = false; };
  }, [status, user, authLoading]);

  // Whitelist paths regardless of decision
  const isPublicPath = PUBLIC_PATHS.includes(location.pathname)
    || PUBLIC_PREFIXES.some((p) => location.pathname.startsWith(p));

  // Wait for either auth resolution OR our own check to complete before
  // rendering anything — prevents a flash of full app then redirect.
  if (authLoading || (checking && !isPublicPath)) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center" data-testid="beta-gate-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#ff4500]" />
      </div>
    );
  }

  if (status === "block" && !isPublicPath) {
    return <Navigate to={`/beta?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  return children;
}
